<template>
  <div class="LkMunMehReviewForm">
    <Header
      v-model="currentTab"
      :mun-meh-form-id="formId"
      :loading="isLoadingForm"
      v-bind="$attrs"
      :read-only="viewOnly || isSubmitted"
      @click:next="goToFill"
      @action:go-to-missed="goToMissed"
    />

    <v-skeleton-loader
      :loading="isLoadingForm"
      type="article, actions"
      :types="{ actions: 'button@4' }"
      :style="isLoadingForm ? 'padding-top: 16px;' : ''"
    >
      <v-tabs-items v-model="currentTab" class="LkMunMehReviewForm__tab-items">
        <v-tab-item
          v-for="(item, index) in content"
          :key="index"
          :value="index"
          :transition="false"
        >
          <component
            :is="item.component"
            :ref="item.ref || null"
            v-bind="item.attrs || $attrs"
            v-on="item.listeners || $listeners"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-skeleton-loader>
  </div>
</template>

<script>
import Header from '@/views/LKMunMeh/Review/components/LKMunMehReviewHeader.vue'

const FormContent = () => import(
  /* webpackChunkName: "LKMunMehReviewFormContent" */
  '@/views/LKMunMeh/Review/components/LKMunMehReviewFormContent.vue'
)

const FormLogs = () => import(
  /* webpackChunkName: "LKMunMehReviewFormLogs" */
  '@/views/LKMunMeh/Review/components/LKMunMehReviewFormLogs.vue'
)

import { VIEW } from '@/helpers/enums/view-types'
import { MunMehFormStatuses } from '@/typings/api/MunMeh'

import { LKMunMehReviewMapper as StoreMapper } from '../store'

export default {
  name: 'LKMunMehFormReview',
  components: {
    Header,
  },
  props: {
    formId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      isLoadingForm: false,
      form: null,
      currentTab: 0,
    }
  },
  computed: {
    formContentComponent() {
      return FormContent
    },
    content() {
      return [
        {
          ref: 'FormContent',
          attrs: {
            ...this.$attrs,
            form: this.form,
          },
          component: this.formContentComponent,
        },
        {
          component: FormLogs,
          attrs: {
            ...this.$attrs,
            form: this.form,
          },
          listeners: {
            'action:view': this.onJumpToIndicator,
          },
        },
      ]
    },
    munMeh() {
      return this.$attrs['mun-meh']
    },
    viewOnly() {
      return this.$route.params?.viewType === VIEW
    },
    isSubmitted() {
      return [MunMehFormStatuses.submitted, MunMehFormStatuses.done].includes(
        this.munMeh.form?.status,
      )
    },
  },
  watch: {
    formId: {
      async handler(v) {
        if (!v) return
        await this.onGetForm(v)
      },
      immediate: true,
    },
  },
  methods: {
    ...StoreMapper.mapActions([
      'getForm',
      'getFormMarks',
      'getFormLoggedIndicators',
    ]),
    goToFill() {
      if (this.currentTab === 0) {
        this.$refs.FormContent[0]?.goToFill()
      }
    },
    goToMissed() {
      if (this.currentTab === 0) {
        this.$refs.FormContent[0]?.goToMissed()
      }
    },
    onJumpToIndicator(v) {
      const { indicator_id } = v
      if (indicator_id) {
        const formContent = this.$refs.FormContent[0]
        const url = formContent?.getIndicatorUrlById(indicator_id)
        if (url) {
          this.currentTab = 0
          formContent?.goToIndicatorByUrl(url)
        }
      }
    },
    async onGetForm(id) {
      this.isLoadingForm = true

      try {
        this.form = await this.getForm(id)

        const { munMehId, formId } = this.$route.params
        await this.getFormMarks({ munMehId, formId })
        await this.getFormLoggedIndicators(formId)
      } catch (e) {
        this.$notice.errors(e)
      }

      this.isLoadingForm = false
    },
  },
}
</script>

<style lang="scss">
.LkMunMehReviewForm {
  @import "~@/views/LKMunMeh/styles/_MunMehFormStylesheet.scss";
}
</style>
